import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Snackbar,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { TimerIcon } from '@assets/icons/timer';
import { formatTime } from '@utils/formatTime';
import { useNavigate } from '@tanstack/react-router';

const TOTAL_TIME = 15 * 60 * 1000;
const WARNING_THRESHOLDS = [
  { seconds: 5 * 60, type: 'warning5' },
  { seconds: 2 * 60, type: 'warning2' },
  { seconds: 60, type: 'warning60' },
] as const;

const ToastTimer = () => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME / 1000);
  const [isExpired, setIsExpired] = useState(false);
  const [warnings, setWarnings] = useState({
    warning5: false,
    warning2: false,
    warning60: false,
  });

  const endTimeRef = useRef<number | null>(null);
  const titleBlinkRef = useRef<NodeJS.Timeout | null>(null);
  const originalTitle = useRef(document.title);

  const blinkTitle = useCallback((text = '⚠️ Sessão expirando!') => {
    if (titleBlinkRef.current) {
      clearInterval(titleBlinkRef.current);
    }
    let isOriginal = true;
    titleBlinkRef.current = setInterval(() => {
      document.title = isOriginal ? text : originalTitle.current;
      isOriginal = !isOriginal;
    }, 1000);
  }, []);

  const stopTitleBlink = useCallback(() => {
    if (titleBlinkRef.current) {
      clearInterval(titleBlinkRef.current);
      titleBlinkRef.current = null;
    }
    document.title = originalTitle.current;
  }, []);

  const closeWarning = useCallback(
    (type: keyof typeof warnings) => {
      setWarnings((prev) => {
        const updated = { ...prev, [type]: false };
        const anyActive = Object.values(updated).some(Boolean);
        if (!anyActive) stopTitleBlink();
        return updated;
      });
    },
    [stopTitleBlink],
  );

  const handleExpiration = useCallback(() => {
    setIsExpired(true);
    setWarnings({ warning5: false, warning2: false, warning60: false });
    localStorage.removeItem('asasAccessToken');
    localStorage.removeItem('consultaCredito');
    blinkTitle('⚠️ Sessão expirada!');
  }, [blinkTitle]);

  const extendTime = useCallback(() => {
    endTimeRef.current = Date.now() + TOTAL_TIME;
    setIsExpired(false);
    setWarnings({ warning5: false, warning2: false, warning60: false });
    stopTitleBlink();
  }, [stopTitleBlink]);

  useEffect(() => {
    if (!endTimeRef.current) {
      endTimeRef.current = Date.now() + TOTAL_TIME;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((endTimeRef.current! - now) / 1000));
      setTimeLeft(diff);

      if (diff === 0) {
        clearInterval(interval);
        handleExpiration();
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      stopTitleBlink();
    };
  }, [handleExpiration, stopTitleBlink]);

  useEffect(() => {
    if (timeLeft === 0 || isExpired) return;

    const match = WARNING_THRESHOLDS.find((t) => timeLeft === t.seconds);
    if (match) {
      setWarnings((prev) => ({ ...prev, [match.type]: true }));
      blinkTitle();
      if (match.type === 'warning5' || match.type === 'warning2') {
        setTimeout(() => {
          setWarnings((prev) => ({ ...prev, [match.type]: false }));
          stopTitleBlink();
        }, 30000);
      }
    }
  }, [timeLeft, isExpired, blinkTitle, stopTitleBlink]);

  return (
    <>
      {/* ...existing code... */}

      <Snackbar
        open={warnings.warning5}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 1301, left: '12px', right: '12px' }}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          closeWarning('warning5');
        }}
        autoHideDuration={null}
      >
        <Alert
          severity="warning"
          variant="filled"
          sx={{
            backgroundColor: '#C5ECFF',
            padding: '0px',
            borderRadius: '13px',
            '& .MuiAlert-icon': { display: 'none' },
            '& .MuiAlert-message': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0px',
              borderRadius: '13px',
              position: 'relative',
              border: '1px solid #209DDB',
              maxWidth: { xs: '351px', md: '100%' },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '28px',
              py: { xs: '46px', md: '35px' },
              backgroundColor: '#209DDB',
            }}
          >
            <TimerIcon />
          </Box>
          <Box sx={{ padding: '20px 20px', paddingRight: { md: '127px' } }}>
            <Typography
              sx={{
                color: '#209DDB',
                fontSize: '14px',
                fontWeight: 700,
                mb: '15px',
              }}
            >
              Sua sessão expira em {formatTime(timeLeft)} minutos
            </Typography>
            <Button
              size="large"
              variant="outlined"
              onClick={extendTime}
              sx={{
                color: '#209DDB',
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: 'Montserrat, sans-serif',
                maxHeight: '33px',
                textTransform: 'none',
                padding: '14px 16px',
                borderColor: '#209DDB',
                '&:hover': { backgroundColor: '#209DDB21' },
              }}
            >
              Estender sessão
            </Button>
          </Box>
          <Box
            onClick={() => closeWarning('warning5')}
            sx={{
              color: '#209DDB',
              fontSize: '24px',
              position: 'absolute',
              top: '4px',
              right: '12px',
              cursor: 'pointer',
            }}
          >
            ✕
          </Box>
        </Alert>
      </Snackbar>

      <Snackbar
        open={warnings.warning2}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 1301, left: '12px', right: '12px' }}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          closeWarning('warning2');
        }}
        autoHideDuration={null}
      >
        <Alert
          severity="warning"
          variant="filled"
          sx={{
            backgroundColor: '#FFECD0',
            padding: '0px',
            borderRadius: '13px',
            '& .MuiAlert-icon': { display: 'none' },
            '& .MuiAlert-message': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0px',
              borderRadius: '13px',
              position: 'relative',
              border: '1px solid #FF9500',
              maxWidth: { xs: '351px', md: '100%' },
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: '28px',
              py: { xs: '52px', md: '35px' },
              backgroundColor: '#FF9500',
            }}
          >
            <TimerIcon />
          </Box>
          <Box sx={{ padding: '12px 20px', paddingRight: { md: '127px' } }}>
            <Typography
              sx={{ color: '#FF9500', fontSize: '14px', fontWeight: 700 }}
            >
              Sua sessão expira em {formatTime(timeLeft)} minutos
            </Typography>
            <Typography sx={{ color: '#FF9500', fontSize: '10px', mb: '15px' }}>
              Clique em “Estender sessão” para continuar preenchendo
            </Typography>
            <Button
              size="large"
              variant="outlined"
              onClick={extendTime}
              sx={{
                color: '#FF9500',
                fontSize: '14px',
                fontWeight: 700,
                fontFamily: 'Montserrat, sans-serif',
                maxHeight: '33px',
                textTransform: 'none',
                padding: '8px 16px',
                borderColor: '#FF9500',
                '&:hover': { borderColor: '#FF9500', background: '#ff950021' },
              }}
            >
              Estender sessão
            </Button>
          </Box>
          <Box
            onClick={() => closeWarning('warning2')}
            sx={{
              color: '#FF9500',
              fontSize: '24px',
              position: 'absolute',
              top: '4px',
              right: '12px',
              cursor: 'pointer',
            }}
          >
            ✕
          </Box>
        </Alert>
      </Snackbar>

      <Dialog
        open={warnings.warning60 && !isExpired}
        maxWidth={false}
        sx={{ maxWidth: 1170, mx: 'auto' }}
        PaperProps={{
          sx: {
            borderRadius: '13px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 0, borderRadius: '13px 13px 0 0' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: '24px', md: '32px' },
              backgroundColor: '#680000',
              borderTopLeftRadius: '13px',
              borderTopRightRadius: '13px',
              padding: { xs: '32px 75px', md: '50px 242px' },
              boxShadow: '0px 4px 4px 0px #00000040',
            }}
          >
            <TimerIcon color="#FF0000" height={64} width={52} />
            <Typography
              sx={{
                color: '#FF0000',
                fontSize: { xs: '20px', md: '24px' },
                fontWeight: 700,
              }}
            >
              Sessão expirando!
            </Typography>
          </Box>
          <Box sx={{ padding: '32px 24px', backgroundColor: '#fff' }}>
            <Typography
              sx={{
                color: '#808080',
                fontSize: { xs: '18px', md: '24px' },
                fontWeight: 700,
                textAlign: 'center',
                px: '40px',
                lineHeight: 1,
              }}
              component="p"
            >
              Sua sessão expira em{' '}
              <Typography
                component="span"
                sx={{
                  color: '#FF0000',
                  fontSize: { xs: '18px', md: '24px' },
                  lineHeight: 0,
                  fontWeight: 700,
                }}
              >
                {formatTime(timeLeft)}
              </Typography>{' '}
              segundos.
            </Typography>
            <Typography
              sx={{
                color: '#808080',
                fontSize: { xs: '18px', md: '24px' },
                fontWeight: 700,
                textAlign: 'center',
                px: '24px',
                mb: '36px',
              }}
              component="p"
            >
              Após este tempo, seus dados não serão salvos.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: '16px',
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#004AA0',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 700,
                  maxWidth: { md: '237px' },
                  padding: '14px 0px',
                  width: '100%',
                  textTransform: 'none',
                }}
                onClick={extendTime}
              >
                Estender a sessão
              </Button>
              <Button
                variant="contained"
                sx={{
                  background: '#808080',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 700,
                  maxWidth: { md: '237px' },
                  padding: '14px 0px',
                  width: '100%',
                  textTransform: 'none',
                }}
              >
                Sair da sessão
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isExpired}
        maxWidth={false}
        sx={{ maxWidth: 1170, mx: 'auto' }}
        PaperProps={{
          sx: {
            borderRadius: '13px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: 0, borderRadius: '13px 13px 0 0' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: { xs: 'column', md: 'row' },
              backgroundColor: '#680000',
              borderTopLeftRadius: '13px',
              borderTopRightRadius: '13px',
              padding: { xs: '24px 75px', sm: '50px' },
              boxShadow: '0px 4px 4px 0px #00000040',
            }}
          >
            <Typography
              sx={{
                color: '#FF0000',
                fontSize: { xs: '20px', md: '24px' },
                fontWeight: 700,
              }}
            >
              Sessão expirada!
            </Typography>
          </Box>
          <Box
            sx={{
              padding: { xs: '32px 25px', sm: '40px 20px' },
              textAlign: 'center',
              backgroundColor: '#fff',
            }}
          >
            <Typography
              component="p"
              sx={{
                color: '#808080',
                fontSize: { xs: '18px', sm: '24px' },
                fontWeight: 700,
                mb: '13px',
              }}
            >
              Por segurança, sua sessão foi encerrada após 15 minutos de
              inatividade.
            </Typography>
            <Typography
              component="p"
              sx={{ color: '#808080', fontSize: '14px', mb: '8px' }}
            >
              Para contratar o seguro garantia, você precisará fazer uma nova
              consulta de limite.
            </Typography>
            <Typography
              component="p"
              sx={{ color: '#808080', fontSize: '14px', mb: '50px' }}
            >
              Seus dados{' '}
              <Typography component="span" sx={{ fontWeight: 700 }}>
                não
              </Typography>{' '}
              foram salvos por segurança.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: '16px',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#004AA0',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'none',
                  padding: '14px 0px',
                  width: '100%',
                  maxWidth: { md: '237px' },
                }}
                onClick={() => {
                  stopTitleBlink();
                  localStorage.removeItem('asasAccessToken');
                  localStorage.removeItem('consultaCredito');
                  navigate({ to: '/' });
                }}
              >
                Fazer nova Consulta
              </Button>
              <Button
                variant="contained"
                sx={{
                  background: '#808080',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'none',
                  padding: '14px 0px',
                  width: '100%',
                  maxWidth: { md: '237px' },
                  '&:hover': { backgroundColor: 'rgba(92, 92, 92, 0.9)' },
                }}
                onClick={() => {
                  stopTitleBlink();
                  localStorage.removeItem('asasAccessToken');
                  localStorage.removeItem('consultaCredito');
                  navigate({ to: '/' });
                }}
              >
                Voltar ao Jornal
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ToastTimer;
